import React, { useEffect, useState, useCallback, useRef, useContext, useMemo } from 'react';
import ReactDOM from "react-dom/client";
import { Box } from '@mui/material';
import DataGrid from 'devextreme-react/data-grid';
import BigNumber from 'bignumber.js';
import * as DxLoadPanel from 'devextreme-react/load-panel';
import * as Excel from 'exceljs';
import * as FileSaver from 'file-saver';
import { PricelistApis } from '../../../../hooks/apis/PricelistApis';
import { IMasterPricelistData } from '../../../../interfaces/Pricelist/IMasterPricelistData';
import PricelistFilter from '../PricelistFilter/PricelistFilter';
import PageTitle from '../../../components/PageTitle';
import { OverlayContext } from '../../../../contextProviders/OverlayContext';
import MasterPricelistDetailView from './components/MasterPricelistDetailView';

import './MasterPricelist.scss';

// Static — never depend on component state, so hoist entirely outside the component
const scrollingConfig = { scrollByContent: false, scrollByThumb: false };
const pagingConfig = { enabled: true, pageSize: 25 };
const groupPanelConfig = { visible: false };
const headerFilterConfig = { visible: true };
const selectionConfig = { mode: "single" as const };
const loadPanelConfig = { enabled: false };
const sortingConfig = { mode: "multiple" as const };

// Template captures nothing from component scope, safe to hoist fully
const masterDetailConfig = {
  enabled: true,
  template: (container: HTMLElement, options: any) => {
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    const root = ReactDOM.createRoot(container);
    root.render(<MasterPricelistDetailView {...options?.data} />);
  },
};

const MasterPricelist = () => {
  const pageGrid = useRef(null);
  const { setShowLoadingOverlay } = useContext(OverlayContext);
  const { getMasterPricelist } = PricelistApis();
  const [pricelist, setPricelist] = useState<IMasterPricelistData[] | undefined>(null);
  const [filteredPricelist, setFilteredPricelist] = useState<IMasterPricelistData[] | undefined>(null);
  const [textFilter, setTextFilter] = useState<string>('');
  const [selectFilter, setSelectFilter] = useState<string[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    getMasterPricelist().then((data) => {
      if (!data.Message) {
        setPricelist(data);
      } else {
        // Do something with the error message on the page
      }
      setDataLoading(false);
    });
  }, [getMasterPricelist]);

  const setCategoryCellTemplate = useCallback(
    (cellElement, info) => {
      const data = pricelist.find((o) => o.CategoryOrder === info.data.key);
      cellElement.prepend(data.Category);
    },
    [pricelist]
  );

  const setSubCategoryCellTemplate = useCallback(
    (cellElement, info) => {
      const data = pricelist.find((o) => o.SubCategoryOrder === info.data.key);
      cellElement.prepend(data.SubCategory);
    },
    [pricelist]
  );

  const setGroupCellTemplate = useCallback(
    (cellElement, info) => {
      const data = pricelist.find((o) => o.GroupOrder === info.data.key);
      cellElement.prepend(data.Group);
    },
    [pricelist]
  );

  const dataRowPrepared = useCallback((e) => {
    if (e?.rowType === 'group') {
      if (e?.groupIndex === 0) {
        e.rowElement.style.background = '#333';
        e.rowElement.style.color = '#FFF';
      }

      if (e?.groupIndex === 1) {
        e.rowElement.style.background = '#015b40';
        e.rowElement.style.color = '#FFF';
      }

      if (e?.groupIndex === 2) {
        e.rowElement.style.background = '#569149';
        e.rowElement.style.color = '#FFF';
      }
    }
  }, []);

  const collectGroups = useCallback(() => {
    const groupArray = [];
    pricelist.forEach((f) => {
      groupArray.push(f.Group);
    });
    return [...new Set(groupArray)];
  }, [pricelist]);

  const getRowData = useCallback(
    (group) => {
      const groupData = pricelist.filter((f) => f.Group === group);

      const returnData = [];
      groupData.forEach((f) => {
        const analysis = [];
        analysis.push(f.EnviroCode);
        analysis.push(f.Description);
        analysis.push(f.MrlRange);
        analysis.push(f.Facility);
        analysis.push(f.Price ? new BigNumber(f.Price).dp(2, BigNumber.ROUND_HALF_UP).toFixed(2) : "");

        for (let discount = -75; discount <= 25; discount++) {
          if (f.IsFixedPrice) {
            analysis.push(f.Price);
          } else {
            const finalPrice = f.Price * (1 - discount / 100);
            analysis.push(finalPrice ? new BigNumber(finalPrice).dp(2, BigNumber.ROUND_HALF_UP).toFixed(2) : "");
          }
        }
        returnData.push(analysis);
      });

      return returnData;
    },
    [pricelist]
  );

  const getPageHeader = useCallback(() => {
    const rowValues = [];
    rowValues.push('Code');
    rowValues.push('Description');
    rowValues.push('MrlRange');
    rowValues.push('Facility');
    rowValues.push('BP');

    for (let discount = -75; discount <= 25; discount++) {
      const rowTitle = discount < 0 ? 'BP' + Math.abs(discount) : discount === 0 ? 'BASE' : 'BM' + discount;
      rowValues.push(rowTitle);
    }

    return rowValues;
  }, []);

  const exportPricelist = useCallback(async () => {
    setShowLoadingOverlay(true);
    const workbook = new Excel.Workbook();
    collectGroups().forEach((f) => {
      const sheet = workbook.addWorksheet(f);
      sheet.columns = [
        { width: 20 },
        { width: 50 },
        { width: 20 },
        { width: 30 }
      ];

      const headerRow = sheet.addRow(getPageHeader());
      headerRow.font = { bold: true };

      const rowData = getRowData(f);
      sheet.addRows(rowData);
    });

    workbook.xlsx
      .writeBuffer()
      .then((buffer) => FileSaver.saveAs(new Blob([buffer]), `MasterPricelist.xlsx`))
      .catch((err) => {
        throw `Error writing excel export ${err}`;
      })
      .finally(() => setShowLoadingOverlay(false));
  }, [collectGroups, getPageHeader, getRowData, setShowLoadingOverlay]);

  useEffect(() => {
    if (!pricelist) return;

    let filteredData = [...pricelist];

    // Apply select filters (Group filtering)
    if (selectFilter?.length > 0) {
      filteredData = filteredData.filter((item) => selectFilter.includes(item.Group));
    }

    // Apply text search (mimicking DataGrid.searchByText)
    if (textFilter) {
      const lowerCaseSearch = textFilter.toLowerCase();

      filteredData = filteredData.filter((item) =>
        Object.keys(item).some((key) =>
          typeof item[key] === "string" && item[key]?.toLowerCase().includes(lowerCaseSearch)
        )
      );
    }

    setFilteredPricelist(filteredData);
  }, [pricelist, textFilter, selectFilter]);

  useEffect(() => {
    pageGrid.current?.instance.pageIndex(0);
  }, [filteredPricelist]);

  const columns = useMemo(() => [
    { dataField: "Group", groupIndex: 0, groupCellTemplate: setGroupCellTemplate, calculateGroupValue: "GroupOrder", dataType: "string" as const },
    { dataField: "Category", groupIndex: 1, groupCellTemplate: setCategoryCellTemplate, calculateGroupValue: "CategoryOrder", dataType: "string" as const },
    { dataField: "SubCategory", groupIndex: 2, groupCellTemplate: setSubCategoryCellTemplate, calculateGroupValue: "SubCategoryOrder", dataType: "string" as const },
    { dataField: "EnviroCode", caption: "Code", sortOrder: "asc" as const, width: 100, dataType: "string" as const },
    { dataField: "Description", dataType: "string" as const, minWidth: 300 },
    { dataField: "Matrix", alignment: "center" as const, dataType: "string" as const, width: 100 },
    {
      caption: "Price",
      width: 80,
      alignment: "right" as const,
      dataType: "number" as const,
      cellTemplate: (container: HTMLElement, options: any) => {
        const value = options?.data?.Price;
        const formattedPrice = value
          ? `$${new BigNumber(value).dp(2, BigNumber.ROUND_HALF_UP).toFixed(2)}`
          : "POA";
        const root = ReactDOM.createRoot(container);
        root.render(<Box>{formattedPrice}</Box>);
      },
    },
    {
      allowFiltering: false,
      width: 600,
      dataField: "AnalysisComments",
      caption: "Analysis Comments and References*",
      dataType: "string" as const,
      cellTemplate: (container: HTMLElement, options: any) => {
        const root = ReactDOM.createRoot(container);
        root.render(<Box className="analysisComments">{options?.data?.AnalysisComments}</Box>);
      },
    },
    { dataField: "Analysis", visible: false, dataType: "string" as const },
    { dataField: "Analytes", visible: false, dataType: "string" as const },
    { dataField: "AllAnalysis", visible: false, dataType: "string" as const },
  ], [setGroupCellTemplate, setCategoryCellTemplate, setSubCategoryCellTemplate]);

  return (
    <>
      <PageTitle pageTitle="Master Pricelist" />

      <Box className="pricelist">
        <PricelistFilter
          exportPricelist={exportPricelist}
          textFilter={textFilter}
          setTextFilter={setTextFilter}
          setSelectFilter={setSelectFilter}
        />

        <DxLoadPanel.LoadPanel
          height={100}
          width={250}
          message="Loading Master Pricelist"
          visible={dataLoading}
          position={{ of: '#masterGrid' }}
        />

        <DataGrid
          id="masterGrid"
          ref={pageGrid}
          height={780}
          dataSource={filteredPricelist}
          allowColumnReordering={true}
          rowAlternationEnabled={true}
          showBorders={false}
          wordWrapEnabled={true}
          hoverStateEnabled={false}
          onRowPrepared={dataRowPrepared}
          scrolling={scrollingConfig}
          paging={pagingConfig}
          groupPanel={groupPanelConfig}
          headerFilter={headerFilterConfig}
          selection={selectionConfig}
          loadPanel={loadPanelConfig}
          sorting={sortingConfig}
          columns={columns}
          masterDetail={masterDetailConfig}
        />

        <Box className="disclaimerText">* Please note listed references do not imply NATA accreditation to said reference methods or standards.</Box>
      </Box>
    </>
  );
};

export default React.memo(MasterPricelist);